// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { AccountType } from '@shared/utils/Account/AccountType';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { QuoteValid } from '@shared/utils/Quotes/QuoteValid';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { DataCache } from '../DataCache';
export class PositionBalanceID {
    constructor(tradableID, productType) {
        this.tradableID = tradableID;
        this.productType = productType;
    }
    get ProductType() {
        return this.productType;
    }
    equals(other) {
        return this.productType === other.productType && this.tradableID === other.tradableID;
    }
    toString() {
        return `${this.tradableID}-${this.productType}`;
    }
}
export class PositionsBalance {
    constructor() {
        this.Positions = {};
        this.StrikePrice = null;
        this.LongQty = null; // always in lots
        this.ShortQty = null; // always in lots
        this.GrossPnL = null;
        this.NetPositionQty = null; // always in lots
        this.PositionValue = null;
        this.GrossExposure = null;
        this.BreakEvenPoint = null;
        this.AverageLong = null;
        this.AverageShort = null;
        this.NetExposure = null;
        this.CurrentPrice = null;
        this.NetPnL = null;
        this.ShortFullCost = null;
        this.LongFullCost = null;
        this.isMultiAssets = false;
        this.maximumAmountPrecisionAccount = null;
        this._positionBalanceID = null;
    }
    get positionBalanceID() {
        if (!isNullOrUndefined(this._positionBalanceID)) {
            return this._positionBalanceID;
        }
        return new PositionBalanceID(-1, ProductType.General);
    }
    get Instrument() {
        var _a;
        return (_a = Object.values(this.Positions)[0]) === null || _a === void 0 ? void 0 : _a.Instrument;
    }
    get Position() {
        return Object.values(this.Positions)[0];
    }
    get Account() {
        var _a;
        return (_a = Object.values(this.Positions)[0]) === null || _a === void 0 ? void 0 : _a.Account;
    }
    getAccountWithMaxAmountPrecision() {
        return this.maximumAmountPrecisionAccount;
    }
    get ProductType() {
        return this.positionBalanceID.ProductType;
    }
    get hasSameRoute() {
        const positionValues = Object.values(this.Positions);
        if (positionValues.length > 0) {
            const currentRouteID = positionValues[0].Instrument.Route;
            for (const position of positionValues) {
                if (currentRouteID !== position.Instrument.Route) {
                    return false;
                }
            }
        }
        return true;
    }
    removePosition(position) {
        delete this.Positions[position.PositionId];
        if (Object.keys(this.Positions).length !== 0) {
            this.RecalculatePositionBalance();
            this.updateAccountWithMaximumAmountPrecision();
        }
    }
    addPosition(position) {
        if (isNullOrUndefined(this._positionBalanceID)) {
            this._positionBalanceID = new PositionBalanceID(position.Instrument.InstrumentTradableID, position.ProductType);
        }
        this.Positions[position.PositionId] = position;
        this.CreatedDate = Math.min(position.UTCDateTime, this.CreatedDate);
        this.RecalculatePositionBalance();
        this.updateAccountWithMaximumAmountPrecision();
    }
    Dispose() { }
    // TODO remove??? not in use
    populatePositions(accountID, interiorID) {
        const tempPositions = {};
        const positions = DataCache.Trading.getAllPositions();
        for (const position of positions) {
            if (position.Account.AcctNumber === accountID.toString() &&
                position.Instrument.GetInteriorID() === interiorID) {
                tempPositions[position.PositionId] = position;
            }
        }
        this.Positions = tempPositions;
        this.RecalculatePositionBalance();
        this.updateAccountWithMaximumAmountPrecision();
    }
    RecalculatePositionBalance() {
        var _a;
        let GrossPnLTemp = 0;
        let NetPnLTemp = 0;
        let ShortExposureSum = 0;
        let LongExposureSum = 0;
        let ShortFullCostTemp = 0;
        let LongFullCostTemp = 0;
        let PositionValue = 0;
        let LongQtyTemp = 0;
        let ShortQtyTemp = 0;
        let AssetName;
        for (const position of Object.values(this.Positions)) {
            const account = position.Account;
            const positionAssetName = account.AccountType === AccountType.MultiAsset
                ? position.Instrument.Exp2
                : account.assetBalanceDefault.Asset.Name;
            if (AssetName === undefined) {
                AssetName = positionAssetName;
            }
            if (AssetName !== positionAssetName) {
                this.isMultiAssets = true;
                break;
            }
        }
        const calculateInServerCurrency = this.isMultiAssets;
        if (calculateInServerCurrency) {
            this.Asset = DataCache.GetAssetByName(DataCache.baseCurrency);
        }
        else {
            this.Asset = DataCache.GetAssetByName(AssetName);
        }
        for (const position of Object.values(this.Positions)) {
            let recordGrossPnL;
            let recordNetPnL;
            let recordExposureValue;
            let recordPositionValue;
            if (calculateInServerCurrency) {
                recordGrossPnL = position.getGrossPnL(false);
                recordNetPnL = position.getNetPnL(false);
                recordExposureValue = position.getExpositionValue(false);
                recordPositionValue = position.getPositionValue(false);
            }
            else {
                recordGrossPnL = position.getGrossPnL(true);
                recordNetPnL = position.getNetPnL(true);
                recordExposureValue = position.getExpositionValue(true);
                recordPositionValue = position.getPositionValue(true);
            }
            if (position.getGrossPnL(true) !== 0) {
                GrossPnLTemp += recordGrossPnL;
            }
            if (position.getNetPnL(true) !== 0) {
                NetPnLTemp += recordNetPnL;
            }
            if (position.BuySell === OperationType.Buy) {
                LongQtyTemp += position.Amount;
                LongExposureSum += recordExposureValue;
                LongFullCostTemp += position.Amount * position.Price;
            }
            else {
                ShortQtyTemp += position.Amount;
                ShortExposureSum += recordExposureValue;
                ShortFullCostTemp += position.Amount * position.Price;
            }
            PositionValue += recordPositionValue;
        }
        this.Symbol = this.Position.GetInstrumentDisplayName();
        this.ExpDate = ((_a = this.Instrument) === null || _a === void 0 ? void 0 : _a.ExpDate)
            ? DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(new Date(this.Instrument.ExpDateReal.getTime()))
            : null;
        this.StrikePrice = this.Instrument != null && this.Instrument.StrikePrice > 0 ? this.Instrument.StrikePrice : 0;
        this.LongQty = LongQtyTemp;
        this.ShortQty = ShortQtyTemp;
        this.GrossPnL = GrossPnLTemp;
        this.NetPnL = NetPnLTemp;
        this.ShortFullCost = ShortFullCostTemp;
        this.LongFullCost = LongFullCostTemp;
        this.NetPositionQty = LongQtyTemp - ShortQtyTemp;
        this.PositionValue = PositionValue;
        const LongAndShortExposure = LongExposureSum - ShortExposureSum;
        const LongAndShortFullCost = LongFullCostTemp - ShortFullCostTemp;
        this.GrossExposure = LongExposureSum + ShortExposureSum;
        if (this.NetPositionQty !== undefined && this.NetPositionQty !== 0) {
            if (LongAndShortFullCost === 0) {
                this.BreakEvenPoint = 0;
            }
            else {
                const breakeven = LongAndShortFullCost / this.NetPositionQty;
                this.BreakEvenPoint = breakeven >= 0 ? breakeven : NaN;
            }
        }
        else {
            this.BreakEvenPoint = NaN;
        }
        if (this.LongQty !== undefined) {
            this.AverageLong = LongFullCostTemp / this.LongQty;
        }
        else {
            this.AverageLong = 0;
        }
        if (this.ShortQty !== undefined) {
            this.AverageShort = ShortFullCostTemp / this.ShortQty;
        }
        else {
            this.AverageShort = 0;
        }
        this.NetExposure = LongAndShortExposure;
        this.CurrentPrice = this.GetCurrentPrice(this.Instrument, LongQtyTemp, ShortQtyTemp);
    }
    GetCurrentPrice(instrument, buyAmount, sellAmount) {
        if ((instrument === null || instrument === void 0 ? void 0 : instrument.DataCache) == null) {
            return -1;
        }
        if (instrument.CalcPriceOnTrade()) {
            const lastPrice = instrument.Level1.GetLastPrice();
            if (isNaN(lastPrice) || lastPrice >= 0) {
                return lastPrice;
            }
            return -1;
        }
        else {
            const lastQuote = instrument.GetLastQuote(QuoteValid.Last);
            if (lastQuote == null) {
                return -1;
            }
            let lastPrice = 0;
            if (!lastQuote.IsValid) {
                lastPrice = instrument.Level1.GetLastPrice();
            }
            const sp = instrument.DataCache.GetSpreadPlan(null);
            const ask = !isNaN(lastQuote.Ask) ? lastQuote.AskSpread_SP_Ins(sp, instrument) : lastPrice;
            const bid = !isNaN(lastQuote.Bid) ? lastQuote.BidSpread_SP_Ins(sp, instrument) : lastPrice;
            // чтобы было как в позиции (#39817)
            let curPrice = buyAmount - sellAmount > 0 ? bid : ask;
            if (curPrice == 0) {
                const prevClose = instrument.Level1.GetPrevClose();
                if (prevClose >= 0) {
                    curPrice = prevClose;
                }
            }
            return curPrice;
        }
    }
    updateAccountWithMaximumAmountPrecision() {
        let maxPrecision = -1;
        for (const posID in this.Positions) {
            const pos = this.Positions[posID];
            const currentPrecision = pos.Instrument.getAmountPrecision(null, pos.Account, this.ProductType);
            if (currentPrecision > maxPrecision) {
                maxPrecision = currentPrecision;
                this.maximumAmountPrecisionAccount = pos.Account;
            }
        }
    }
}
